import type { ApiResponse, CommissionStatus, PaginatedCommissions } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

export interface CommissionHistoryFilters {
  page?: number;
  limit?: number;
  status?: CommissionStatus | '';
  affiliateIds?: string[];
  startDate?: string;
  endDate?: string;
}

const fetchCommissionHistory = async (
  filters: CommissionHistoryFilters
): Promise<PaginatedCommissions> => {
  // Clean undefined and empty values
  const params = Object.fromEntries(
    Object.entries(filters).filter(
      ([_, value]) =>
        value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)
    )
  );

  const { data } = await api.get<ApiResponse<PaginatedCommissions>>(
    '/dashboard/affiliates/commissions',
    {
      params,
    }
  );

  if (!data.success || !data.data) {
    throw new Error(data.message || 'Failed to fetch commission history');
  }
  // Return the data directly, as it is already in the expected format
  return data.data;
};

export const useCommissionHistoryQuery = (filters: CommissionHistoryFilters) => {
  const queryKeyFilters = {
    ...filters,
    affiliateIds: filters.affiliateIds ? [...filters.affiliateIds].sort().join(',') : undefined,
  };

  return useQuery<PaginatedCommissions, Error>({
    queryKey: ['commissions', 'history', queryKeyFilters],
    queryFn: () => fetchCommissionHistory(filters),
    placeholderData: (previousData) => previousData,
  });
};
